import { ResponsiveLine } from '@nivo/line'
import { GLoading } from 'components/basic-blocks'
import moment from 'moment'
import { color } from 'utils/colors'
import { LineChartProps } from './types'
import { formatNumberAbbreviation } from '../../../utils'
import { RiLoader2Line } from 'react-icons/ri'
import { useThemeColors } from 'hooks'
import { useNivoTheme } from 'components/charts/nivo-theme'

const themeColorDefinitions = {
	// This file was the only chart on `--color-gray-500`, which is `#b8b9c4` in
	// dark. That is the same ink as the axis text, so the grid read brighter
	// than the data line. `--chart-grid-strong` drops to the faint grid tint in
	// dark and holds the former `#6B7280` in light. Do NOT point this at
	// `--chart-grid`: that token is `#DDD` in light, which at 0.5px is 1.36:1
	// on white and takes the light grid to almost nothing.
	grid: { variable: '--chart-grid-strong', fallback: '#6B7280' },
	// The axis tick labels are TEXT, not a rule, so they keep the old ink.
	// They used to share the `grid` entry, so moving the grid onto the faint
	// `--chart-grid` (#2a253f in dark) took the labels down with it and they
	// went almost invisible. The dark value here is #b8b9c4, the same value
	// the sibling charts get from `--chart-axis-strong`; light is unchanged.
	axisText: { variable: '--color-gray-500', fallback: '#6B7280' },
	areaPurple: { variable: '--chart-area-purple', fallback: '#F1DEFF' },
	areaPurpleEnd: { variable: '--chart-area-purple-end', fallback: '#FFFFFF00' },
	areaCyan: { variable: '--chart-area-cyan', fallback: '#C1F6FF' },
	areaCyanEnd: { variable: '--chart-area-cyan-end', fallback: '#FFFFFF' },
	roseLine: { variable: '--t-rose-100', fallback: '#FFD1D1' },
	brandLine: { variable: '--t-plan-btn', fallback: '#7169FF' }
} as const

const LineChart = ({
	data,
	colors,
	formatter,
	noBottomAxis,
	legend,
	enablePoints = true,
	lineWidth = 3,
	className,
	abbreviate = true
}: LineChartProps) => {
	const themeColors = useThemeColors(themeColorDefinitions)
	const nivoTheme = useNivoTheme()
	if (!data) return <GLoading />

	const coloredData = data.map((d: any, i: number) => {
		d.colorName = colors[i]
		return d
	})
	colors.map((c) => color(c.color, c.shade || 500))
	const theme = {
		...nivoTheme,
		grid: {
			line: {
				stroke: themeColors.grid,
				strokeWidth: 0.5
			}
		}
	}

	return (
		<div className={`${className} h-[250px]`}>
			<ResponsiveLine
				data={coloredData}
				enableGridX={false}
				margin={{ top: 20, right: 0, bottom: 20, left: 40 }}
				colors={[themeColors.roseLine, themeColors.brandLine]}
				curve={'monotoneX'}
				defs={[
					{
						id: 'gradientA',
						type: 'linearGradient',
						colors: [
							{ offset: 0, color: themeColors.areaPurple, opacity: 0.7 },
							{ offset: 100, color: themeColors.areaPurpleEnd, opacity: 0 }
						]
					},
					{
						id: 'gradientB',
						type: 'linearGradient',
						colors: [
							{ offset: 0, color: themeColors.areaCyan, opacity: 1 },
							{ offset: 100, color: themeColors.areaCyanEnd, opacity: 0 }
						]
					}
				]}
				fill={[
					{ match: { id: 'Total saved' }, id: 'gradientA' },
					{ match: { id: 'Total spend' }, id: 'gradientB' }
				]}
				axisLeft={{
					renderTick: (tick) => {
						const value = formatter(tick.value).replace(',', '')
						return (
							<g transform={`translate(${-35},${tick.y - 15})`}>
								<text
									x={1}
									y={2}
									dy={16}
									textAnchor="start"
									fontSize={10}
									fill={themeColors.axisText}
								>
									{abbreviate
										? formatNumberAbbreviation(Number(value) || 0, false, 1)
										: value}
								</text>
							</g>
						)
					}
				}}
				xFormat="time:%Y-%m-%d"
				axisBottom={
					noBottomAxis
						? null
						: {
								tickSize: 5,
								tickPadding: 5,
								tickValues: data[0].data
									.filter((_: any, i: any) => i % 3 === 0)
									.map((d: any) => d.x),
								renderTick: (tick) => {
									const x = tick.x
									const y = tick.y
									return (
										<g transform={`translate(${x},${y})`}>
											<text
												x={1}
												y={1}
												dy={16}
												textAnchor="start"
												fontSize={10}
												fill={themeColors.axisText}
											>
												{moment(tick.value).isValid()
													? moment(tick.value).format('h A')
													: tick.value}
											</text>
										</g>
									)
								},
								tickRotation: -45
						  }
				}
				legends={legend}
				enableGridY={true}
				enablePointLabel={false}
				enableArea={true}
				areaOpacity={0.2}
				areaBlendMode="normal"
				pointSize={8}
				lineWidth={lineWidth}
				enablePoints={enablePoints}
				pointBorderWidth={1}
				useMesh={true}
				enableSlices={false}
				theme={theme}
				tooltip={({ point }) => (
					<div data-theme="light" className="border rounded bg-white shadow-sm p-1 flex space-x-1 items-center text-xs text-t-desc-gray">
						<RiLoader2Line className="w-5 h-5" style={{ color: point.color }} />
						<span>{moment(point.data.x).format('MMM Do')}</span>:
						<span className="font-semibold">
							{formatter(Number(point.data.y))} {point.id.split('.')[0]}{' '}
						</span>
					</div>
				)}
			/>
		</div>
	)
}
export default LineChart
